/**
 * Rax Automation Confirmation Popup
 *
 * Appears in the chat UI when the assistant detects an automation intent.
 * Allows users to confirm, edit, or cancel the automation rule creation.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi, type AutomationRuleDraft, type AutomationRule } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

export interface AutomationIntentDraft {
  name: string;
  triggerType: "schedule" | "event";
  schedule: string | null;
  eventCondition: string | null;
  action: string;
  sourceMessage: string;
  confidence: number;
}

@customElement("rax-automation-confirmation-popup")
export class RaxAutomationConfirmationPopup extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .popup-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(4px);
        z-index: 1000;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        padding: var(--space-4);
        animation: fadeIn 0.2s ease-out;
      }
      
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      
      .popup {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-lg), var(--shadow-glow);
        max-width: 480px;
        width: 100%;
        max-height: 80vh;
        overflow-y: auto;
        animation: slideUp 0.25s ease-out;
      }
      
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(24px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .popup-header {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-4);
        border-bottom: 1px solid var(--border-color);
      }
      
      .popup-icon {
        width: 40px;
        height: 40px;
        border-radius: var(--radius-md);
        background: linear-gradient(135deg, var(--rax-yellow), var(--rax-gold));
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--font-xl);
      }
      
      .popup-title {
        flex: 1;
      }
      
      .popup-title h3 {
        margin: 0;
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .popup-title p {
        margin: var(--space-1) 0 0;
        font-size: var(--font-sm);
        color: var(--text-secondary);
      }
      
      .popup-close {
        background: none;
        border: none;
        color: var(--text-tertiary);
        cursor: pointer;
        padding: var(--space-2);
        font-size: var(--font-xl);
        line-height: 1;
        transition: color var(--transition-fast);
      }
      
      .popup-close:hover {
        color: var(--text-primary);
      }
      
      .popup-body {
        padding: var(--space-4);
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
      }
      
      .source-message {
        background: var(--bg-subtle);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        font-size: var(--font-sm);
        color: var(--text-secondary);
        border-left: 3px solid var(--rax-yellow);
      }
      
      .source-message::before {
        content: "Your message:";
        display: block;
        font-weight: 600;
        color: var(--text-tertiary);
        margin-bottom: var(--space-2);
        font-size: var(--font-xs);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      
      .field {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .field label {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-secondary);
      }
      
      .field input,
      .field textarea,
      .field select {
        background: var(--bg-subtle);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        font-size: var(--font-sm);
        color: var(--text-primary);
        transition: border-color var(--transition-fast);
      }
      
      .field input:focus,
      .field textarea:focus,
      .field select:focus {
        outline: none;
        border-color: var(--rax-yellow);
        box-shadow: 0 0 0 2px var(--accent-glow);
      }
      
      .field textarea {
        min-height: 80px;
        resize: vertical;
      }
      
      .trigger-type-selector {
        display: flex;
        gap: var(--space-2);
      }
      
      .trigger-option {
        flex: 1;
        padding: var(--space-3);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        background: var(--bg-subtle);
        cursor: pointer;
        transition: all var(--transition-fast);
        text-align: center;
      }
      
      .trigger-option.selected {
        border-color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.1);
      }
      
      .trigger-option:hover:not(.selected) {
        border-color: var(--text-tertiary);
      }
      
      .trigger-option-icon {
        font-size: var(--font-xl);
        margin-bottom: var(--space-1);
      }
      
      .trigger-option-label {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .trigger-option-desc {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        margin-top: var(--space-1);
      }
      
      .confidence-indicator {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .confidence-bar {
        flex: 1;
        height: 4px;
        background: var(--bg-subtle);
        border-radius: var(--radius-full);
        overflow: hidden;
      }
      
      .confidence-fill {
        height: 100%;
        background: var(--rax-yellow);
        transition: width var(--transition-normal);
      }
      
      .popup-footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-3);
        padding: var(--space-4);
        border-top: 1px solid var(--border-color);
      }
      
      .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-md);
        font-weight: 600;
        font-size: var(--font-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
        border: none;
      }
      
      .btn-primary {
        background: var(--rax-yellow);
        color: var(--rax-black);
      }
      
      .btn-primary:hover {
        background: var(--rax-gold);
      }
      
      .btn-primary:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .btn-secondary {
        background: transparent;
        border: 1px solid var(--border-color);
        color: var(--text-secondary);
      }
      
      .btn-secondary:hover {
        border-color: var(--text-tertiary);
        color: var(--text-primary);
      }
      
      .btn-ghost {
        background: transparent;
        color: var(--text-tertiary);
      }
      
      .btn-ghost:hover {
        color: var(--text-primary);
      }
      
      .error-message {
        background: rgba(239, 68, 68, 0.14);
        border: 1px solid rgba(239, 68, 68, 0.55);
        color: #fca5a5;
        padding: var(--space-3);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
      }
      
      @media (max-width: 640px) {
        .popup-overlay {
          align-items: flex-end;
          padding: 0;
        }
      
        .popup {
          max-height: 90vh;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
      }
    `,
  ];

  @property({ type: Object })
  draft: AutomationIntentDraft | null = null;

  @property({ type: String })
  agentId: string | null = null;

  @state()
  private editedName: string = "";

  @state()
  private editedTriggerType: "schedule" | "event" = "schedule";

  @state()
  private editedSchedule: string = "";

  @state()
  private editedEventCondition: string = "";

  @state()
  private editedAction: string = "";

  @state()
  private saving = false;

  @state()
  private error: string | null = null;

  override updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has("draft") && this.draft) {
      this.editedName = this.draft.name;
      this.editedTriggerType = this.draft.triggerType;
      this.editedSchedule = this.draft.schedule ?? "";
      this.editedEventCondition = this.draft.eventCondition ?? "";
      this.editedAction = this.draft.action;
    }
  }

  override render() {
    if (!this.draft) {
      return nothing;
    }

    const confidencePercent = Math.round((this.draft.confidence ?? 0.75) * 100);

    return html`
      <div class="popup-overlay" @click=${this.handleOverlayClick}>
        <div class="popup" @click=${(e: Event) => e.stopPropagation()}>
          <div class="popup-header">
            <div class="popup-icon">&#9889;</div>
            <div class="popup-title">
              <h3>Create Automation?</h3>
              <p>Rax detected an automation in your message</p>
            </div>
            <button class="popup-close" @click=${this.handleCancel} aria-label="Close">
              &times;
            </button>
          </div>

          <div class="popup-body">
            <div class="source-message">
              ${this.draft.sourceMessage}
            </div>

            <div class="confidence-indicator">
              <span>Confidence:</span>
              <div class="confidence-bar">
                <div class="confidence-fill" style="width: ${confidencePercent}%"></div>
              </div>
              <span>${confidencePercent}%</span>
            </div>

            <div class="field">
              <label for="automation-name">Name</label>
              <input
                id="automation-name"
                type="text"
                .value=${this.editedName}
                @input=${(e: Event) => (this.editedName = (e.target as HTMLInputElement).value)}
                placeholder="e.g., Daily summary at 9am"
              />
            </div>

            <div class="field">
              <label>Trigger Type</label>
              <div class="trigger-type-selector">
                <div
                  class="trigger-option ${this.editedTriggerType === "schedule" ? "selected" : ""}"
                  @click=${() => (this.editedTriggerType = "schedule")}
                >
                  <div class="trigger-option-icon">&#128337;</div>
                  <div class="trigger-option-label">Schedule</div>
                  <div class="trigger-option-desc">Run at specific times</div>
                </div>
                <div
                  class="trigger-option ${this.editedTriggerType === "event" ? "selected" : ""}"
                  @click=${() => (this.editedTriggerType = "event")}
                >
                  <div class="trigger-option-icon">&#9889;</div>
                  <div class="trigger-option-label">Event</div>
                  <div class="trigger-option-desc">Run when conditions match</div>
                </div>
              </div>
            </div>

            ${
              this.editedTriggerType === "schedule"
                ? html`
              <div class="field">
                <label for="automation-schedule">Schedule</label>
                <input
                  id="automation-schedule"
                  type="text"
                  .value=${this.editedSchedule}
                  @input=${(e: Event) => (this.editedSchedule = (e.target as HTMLInputElement).value)}
                  placeholder="e.g., every day at 9am, every Monday at 5pm"
                />
              </div>
            `
                : html`
              <div class="field">
                <label for="automation-condition">Condition</label>
                <input
                  id="automation-condition"
                  type="text"
                  .value=${this.editedEventCondition}
                  @input=${(e: Event) => (this.editedEventCondition = (e.target as HTMLInputElement).value)}
                  placeholder="e.g., when I receive an email from boss@company.com"
                />
              </div>
            `
            }

            <div class="field">
              <label for="automation-action">Action</label>
              <textarea
                id="automation-action"
                .value=${this.editedAction}
                @input=${(e: Event) => (this.editedAction = (e.target as HTMLTextAreaElement).value)}
                placeholder="What should Rax do?"
              ></textarea>
            </div>

            ${
              this.error
                ? html`
              <div class="error-message">${this.error}</div>
            `
                : nothing
            }
          </div>

          <div class="popup-footer">
            <button class="btn btn-ghost" @click=${this.handleCancel}>
              Cancel
            </button>
            <button
              class="btn btn-primary"
              ?disabled=${this.saving || !this.isValid()}
              @click=${this.handleConfirm}
            >
              ${this.saving ? "Creating..." : "Create Automation"}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private isValid(): boolean {
    if (!this.editedName.trim()) return false;
    if (!this.editedAction.trim()) return false;
    if (this.editedTriggerType === "schedule" && !this.editedSchedule.trim()) return false;
    if (this.editedTriggerType === "event" && !this.editedEventCondition.trim()) return false;
    return true;
  }

  private handleOverlayClick(e: Event) {
    // Only close if clicking the overlay itself, not the popup
    if (e.target === e.currentTarget) {
      this.handleCancel();
    }
  }

  private handleCancel() {
    this.dispatchEvent(new CustomEvent("cancel"));
  }

  private async handleConfirm() {
    if (this.saving || !this.isValid()) return;

    this.saving = true;
    this.error = null;

    try {
      const payload = {
        name: this.editedName.trim(),
        triggerType: this.editedTriggerType,
        schedule: this.editedTriggerType === "schedule" ? this.editedSchedule.trim() : undefined,
        eventCondition:
          this.editedTriggerType === "event" ? this.editedEventCondition.trim() : undefined,
        action: this.editedAction.trim(),
        sourceMessage: this.draft?.sourceMessage,
        agentId: this.agentId ?? undefined,
      };

      const response = await raxApi.createAutomationRule(payload);

      if (!response.success) {
        this.error = response.error || "Failed to create automation";
        return;
      }

      this.dispatchEvent(
        new CustomEvent("created", {
          detail: { rule: response.data },
        }),
      );
    } catch (err) {
      console.error("[AutomationConfirmation] Error creating rule:", err);
      this.error = err instanceof Error ? err.message : "Failed to create automation";
    } finally {
      this.saving = false;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-automation-confirmation-popup": RaxAutomationConfirmationPopup;
  }
}
